import {
  Anchor,
  Avatar,
  Box,
  Button,
  Card,
  CardSection,
  Center,
  Flex,
  Group,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import CardTitle from "@webapp/components/CardTitle";
import CenteredLoader from "@webapp/components/CenteredLoader";
import EntityNotFound from "@webapp/components/EntityNotFound";
import ImageGallery from "@webapp/components/ImageGallery";
import PageWithHeading from "@webapp/components/layout/PageWithHeading";
import MapsLink from "@webapp/components/MapsLink";
import Matches from "@webapp/components/Matches";
import RankingTable from "@webapp/components/RankingTable";
import { createWebcalLink } from "@webapp/utils/webcal";
import dayjs from "dayjs";
import de from "dayjs/locale/de";
import weekday from "dayjs/plugin/weekday";
import { Fragment, Suspense } from "react";
import { FaBullhorn as IconSubscribe } from "react-icons/fa6";
import {
  useFileUrls,
  useLocations,
  useMembers,
  useSamsMatches,
  useSamsRoster,
  useTeamBySlug,
} from "@webapp/hooks/dataQueries";
import {
  listSamsTeamsFn,
  loadSamsMatchesForSsrFn,
  peekSamsRankingsCacheFn,
} from "@webapp/server/functions/sams";
import { getHomeMembersFn } from "@webapp/server/functions/members";
import { getTeamBySlugFn } from "@webapp/server/functions/teams";
import type { SamsMatchesHookOptions } from "@webapp/utils/sams-ssr";

dayjs.locale(de);
dayjs.extend(weekday);

export const Route = createFileRoute("/_layout/teams/$slug")({
  loader: async ({ params }) => {
    const { slug } = params;
    const [team, samsTeamsResult, members] = await Promise.all([
      getTeamBySlugFn({ data: { slug } }),
      listSamsTeamsFn(),
      getHomeMembersFn(),
    ]);

    if (!team) {
      return { team: null, members, rankings: undefined, matchesQueryOptions: undefined };
    }

    const samsTeam = samsTeamsResult.teams.find((t) => t.uuid === team.sbvvTeamId);

    if (!samsTeam) {
      return {
        team,
        members,
        samsTeam: undefined,
        rankings: undefined,
        matchesQueryOptions: undefined,
      };
    }

    const [rankingsResult, matchesSsr] = await Promise.allSettled([
      samsTeam.leagueUuid
        ? peekSamsRankingsCacheFn({ data: { leagueUuids: [samsTeam.leagueUuid] } })
        : Promise.resolve(undefined),
      loadSamsMatchesForSsrFn({ data: { team: samsTeam.uuid } }),
    ]);

    const rankings = rankingsResult.status === "fulfilled" ? rankingsResult.value : undefined;
    const matchesQueryOptions: SamsMatchesHookOptions =
      matchesSsr.status === "fulfilled" ? matchesSsr.value.hookOptions : { team: samsTeam.uuid };

    return { team, members, samsTeam, rankings, matchesQueryOptions };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { slug } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const { data: team, isLoading, error } = useTeamBySlug(slug, loaderData.team ?? undefined);

  if (isLoading) {
    return (
      <PageWithHeading title="Mannschaft">
        <CenteredLoader text="Lade Mannschaftsdaten..." />
      </PageWithHeading>
    );
  }

  if (error || !team) {
    return (
      <EntityNotFound
        entityName="Mannschaft"
        title="Mannschaft nicht gefunden"
        description="Diese Mannschaft existiert nicht oder wurde entfernt."
      />
    );
  }

  return (
    <PageWithHeading title={team.name} subtitle={team.league || undefined}>
      <Stack>
        <Suspense fallback={<CenteredLoader text="Lade Trainingszeiten..." />}>
          <TeamSchedule team={team} />
        </Suspense>
        <Suspense fallback={<CenteredLoader text="Lade Trainer..." />}>
          <TeamTrainers team={team} initialMembers={loaderData.members} />
        </Suspense>
        <Suspense fallback={null}>
          <TeamRoster teamUuid={loaderData.samsTeam?.uuid} />
        </Suspense>
        <Suspense fallback={<CenteredLoader text="Lade Fotos..." />}>
          <TeamPictures team={team} />
        </Suspense>
        <Suspense fallback={<CenteredLoader text="Lade Tabelle..." />}>
          {loaderData.samsTeam?.leagueUuid && (
            <RankingTable
              leagueUuid={loaderData.samsTeam.leagueUuid}
              initialData={loaderData.rankings?.[0]}
              currentTeamId={loaderData.samsTeam.uuid}
            />
          )}
        </Suspense>
        <Suspense fallback={<CenteredLoader text="Lade Spielplan..." />}>
          <TeamCalendar slug={slug} loaderSamsTeam={loaderData.samsTeam} />
        </Suspense>
        <Suspense fallback={<CenteredLoader text="Lade Spielplan..." />}>
          <TeamMatches
            loaderSamsTeam={loaderData.samsTeam}
            matchesQueryOptions={loaderData.matchesQueryOptions}
          />
        </Suspense>
        <Center>
          <Button component={Link} to="/#mannschaften">
            zu den anderen Mannschaften
          </Button>
        </Center>
      </Stack>
    </PageWithHeading>
  );
}

function TeamCalendar({
  slug,
  loaderSamsTeam,
}: {
  slug: string;
  loaderSamsTeam: ReturnType<typeof Route.useLoaderData>["samsTeam"];
}) {
  if (!loaderSamsTeam) return null;

  const webcalLink = createWebcalLink(`/ics/${slug}.ics`);

  return (
    <Card>
      <CardTitle>Mannschaftskalender</CardTitle>
      <Text>
        <Anchor
          href={webcalLink}
          style={{ display: "inline-flex", gap: 4, alignItems: "baseline" }}
        >
          <IconSubscribe /> Abboniere unseren Kalender
        </Anchor>
        , um neue Termine saisonübergreifend automatisch in deiner Kalender-App zu empfangen.
      </Text>
    </Card>
  );
}

function TeamMatches({
  loaderSamsTeam,
  matchesQueryOptions,
}: {
  loaderSamsTeam: ReturnType<typeof Route.useLoaderData>["samsTeam"];
  matchesQueryOptions: SamsMatchesHookOptions | undefined;
}) {
  const { data: matches, isLoading: isLoadingMatches } = useSamsMatches(
    matchesQueryOptions ?? { team: loaderSamsTeam?.uuid },
  );

  const currentMonth = dayjs().month() + 1;
  const isOffSeason = currentMonth >= 5 && currentMonth <= 9;

  if (isLoadingMatches) {
    return <CenteredLoader text="Lade Spieltermine..." />;
  }

  if (!isLoadingMatches && (!loaderSamsTeam || !matches)) {
    return (
      <Card>
        <CardTitle>Keine Spieltermine gefunden</CardTitle>
        {isOffSeason && (
          <Text>
            Die Saison im Hallenvolleyball findet in der Regel in den Monaten von September bis
            April statt.
          </Text>
        )}
      </Card>
    );
  }

  const futureMatches = matches?.matches.filter((m) => !m.hasResult);
  const pastMatches = matches?.matches.filter((m) => m.hasResult);

  futureMatches?.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
  pastMatches?.sort((a, b) => dayjs(b.date).diff(dayjs(a.date)));

  return (
    <>
      {pastMatches && pastMatches.length > 0 && (
        <Card>
          <CardTitle>Ergebnisse</CardTitle>
          <CardSection p={{ base: undefined, sm: "sm" }}>
            <Matches
              type="past"
              matches={pastMatches}
              timestamp={matches?.timestamp ? new Date(matches.timestamp) : undefined}
              highlightTeamUuid={loaderSamsTeam?.uuid}
              uniqueLeague
            />
          </CardSection>
        </Card>
      )}
      {futureMatches && futureMatches.length > 0 ? (
        <Card>
          <CardTitle>Spielplan</CardTitle>
          <CardSection p={{ base: undefined, sm: "sm" }}>
            <Matches
              type="future"
              matches={futureMatches}
              timestamp={matches?.timestamp ? new Date(matches.timestamp) : undefined}
              highlightTeamUuid={loaderSamsTeam?.uuid}
            />
          </CardSection>
        </Card>
      ) : (
        <Card>
          <CardTitle>Spielplan</CardTitle>
          <Text>Aktuell stehen keine weiteren Spieltermine für diese Saison an.</Text>
          {isOffSeason && (
            <Text>
              Die Saison im Hallenvolleyball findet in der Regel in den Monaten von September bis
              April statt.
            </Text>
          )}
        </Card>
      )}
    </>
  );
}

function TeamSchedule({ team }: { team: NonNullable<ReturnType<typeof useTeamBySlug>["data"]> }) {
  const { data: locations } = useLocations();

  if (!team.trainingSchedules || team.trainingSchedules.length === 0) return null;

  return (
    <Card>
      <Stack>
        <CardTitle>Trainingszeiten</CardTitle>
        <Flex columnGap="xl" rowGap="md" wrap="wrap">
          {team.trainingSchedules.map((schedule) => {
            const location = locations?.items.find((loc) => loc.id === schedule.locationId);
            const dayNames = schedule.days.map((d) => `${dayjs().weekday(d).format("dddd")}s`);
            const separator = dayNames.length > 2 ? ", " : " & ";
            const scheduleKey = `${schedule.days.join("-")}-${schedule.startTime}-${schedule.endTime}`;

            return (
              <Stack key={scheduleKey} gap={0}>
                <Text>
                  {dayNames.join(separator)} {schedule.startTime} - {schedule.endTime} Uhr
                </Text>
                {location && (
                  <MapsLink
                    name={location.name}
                    street={location.street}
                    postal={location.postal}
                    city={location.city}
                  />
                )}
              </Stack>
            );
          })}
        </Flex>
      </Stack>
    </Card>
  );
}

type TeamMember = NonNullable<
  NonNullable<Awaited<ReturnType<typeof useMembers>>["data"]>["items"][number]
>;

function MemberList({
  title,
  memberList,
  avatarUrlByKey,
}: {
  title: string;
  memberList: TeamMember[] | undefined;
  avatarUrlByKey: Map<string, string>;
}) {
  if (!memberList || memberList.length === 0) return null;

  return (
    <Stack>
      <CardTitle>{title}</CardTitle>
      <Flex wrap="wrap" gap="xl">
        {memberList.map((member) => {
          const avatarUrl = member.avatarS3Key ? avatarUrlByKey.get(member.avatarS3Key) : undefined;
          const person = (
            <Group align="center">
              <Avatar src={avatarUrl} name={member.name} />
              <Stack gap={0}>
                <Text fw="bold" c="turquoise">
                  {member.name}
                </Text>
                {member.proxyEmail && (
                  <Text c="dimmed" size="xs">
                    {member.proxyEmail}
                  </Text>
                )}
              </Stack>
            </Group>
          );

          if (member.proxyEmail) {
            return (
              <Anchor key={member.id} href={`mailto:${member.proxyEmail}`} underline="never">
                {person}
              </Anchor>
            );
          }
          return <Fragment key={member.id}>{person}</Fragment>;
        })}
      </Flex>
    </Stack>
  );
}

function TeamTrainers({
  team,
  initialMembers,
}: {
  team: NonNullable<ReturnType<typeof useTeamBySlug>["data"]>;
  initialMembers?: Awaited<ReturnType<typeof useMembers>>["data"];
}) {
  const { data: members } = useMembers(
    initialMembers ? { initialData: initialMembers } : undefined,
  );

  const trainers = team.trainerIds
    ?.map((id) => members?.items.find((m) => m.id === id))
    .filter((x): x is NonNullable<typeof x> => x != null);
  const contacts = team.pointOfContactIds
    ?.map((id) => members?.items.find((m) => m.id === id))
    .filter((x): x is NonNullable<typeof x> => x != null);

  const avatarKeys = [
    ...(trainers?.map((t) => t.avatarS3Key).filter(Boolean) || []),
    ...(contacts?.map((c) => c.avatarS3Key).filter(Boolean) || []),
  ] as string[];
  const { data: avatarUrls } = useFileUrls(avatarKeys);
  const avatarUrlByKey = new Map<string, string>();
  if (avatarUrls) {
    for (let i = 0; i < avatarKeys.length; i++) {
      const url = avatarUrls[i];
      if (url) avatarUrlByKey.set(avatarKeys[i], url);
    }
  }

  if (!trainers?.length && !contacts?.length) {
    return (
      <Card>
        <Text>
          Bei Fragen und Interesse zu dieser Mannschaft, wende dich bitte an{" "}
          <Anchor href="mailto:info@vcmuellheim.de">info@vcmuellheim.de</Anchor>
        </Text>
      </Card>
    );
  }

  return (
    <Card>
      <Flex wrap="wrap" columnGap="xl" rowGap="md">
        <MemberList title="Trainer" memberList={trainers} avatarUrlByKey={avatarUrlByKey} />
        <MemberList
          title={contacts && contacts.length > 1 ? "Ansprechpersonen" : "Ansprechperson"}
          memberList={contacts}
          avatarUrlByKey={avatarUrlByKey}
        />
      </Flex>
    </Card>
  );
}

/** SAMS often returns "Nachname, Vorname" — prefer "Vorname Nachname" for display. */
function parseRosterName(name: string): { first: string; last: string; display: string } {
  const comma = name.indexOf(",");
  if (comma === -1) {
    const trimmed = name.trim();
    return { first: trimmed, last: "", display: trimmed };
  }
  const last = name.slice(0, comma).trim();
  const first = name.slice(comma + 1).trim();
  if (!last || !first) {
    const trimmed = name.trim();
    return { first: trimmed, last: "", display: trimmed };
  }
  return { first, last, display: `${first} ${last}` };
}

function formatRosterName(name: string): string {
  return parseRosterName(name).display;
}

function compareRosterPlayers(
  a: { jerseyNumber?: number; name: string },
  b: { jerseyNumber?: number; name: string },
): number {
  if (a.jerseyNumber != null && b.jerseyNumber != null && a.jerseyNumber !== b.jerseyNumber) {
    return a.jerseyNumber - b.jerseyNumber;
  }
  if ((a.jerseyNumber != null) !== (b.jerseyNumber != null)) {
    return a.jerseyNumber != null ? -1 : 1;
  }
  return parseRosterName(a.name).first.localeCompare(parseRosterName(b.name).first, "de");
}

function JerseyNumber({ number }: { number?: number }) {
  const hasNumber = number != null;
  return (
    <Box
      w={48}
      h={48}
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        background: hasNumber
          ? "var(--mantine-color-blumine-6)"
          : "var(--mantine-color-aquahaze-2)",
        color: hasNumber ? "white" : "var(--mantine-color-dimmed)",
        fontWeight: 800,
        fontSize: hasNumber ? (number >= 10 ? "1.1rem" : "1.35rem") : "1rem",
        letterSpacing: hasNumber ? "-0.02em" : undefined,
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1,
      }}
      aria-label={hasNumber ? `Trikotnummer ${number}` : "Keine Trikotnummer"}
    >
      {hasNumber ? number : "–"}
    </Box>
  );
}

function TeamRoster({ teamUuid }: { teamUuid?: string }) {
  const { data: roster } = useSamsRoster(teamUuid);

  if (!roster) return null;

  const sortedPlayers = [...roster.players].sort(compareRosterPlayers);
  const officialsWithRole = roster.officials.filter((official) => !!official.role?.trim());

  if (!sortedPlayers.length && !officialsWithRole.length) return null;
  return (
    <Card>
      <Stack gap="lg">
        <CardTitle>Kader</CardTitle>
        {sortedPlayers.length > 0 && (
          <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, md: 4 }} spacing="md">
            {sortedPlayers.map((player) => (
              <Group key={player.uuid} gap="sm" wrap="nowrap" align="center">
                {player.portraitImageLink ? (
                  <Avatar src={player.portraitImageLink} name={player.name} size={48} radius="xl" />
                ) : (
                  <JerseyNumber number={player.jerseyNumber} />
                )}
                <Stack gap={2} style={{ minWidth: 0 }}>
                  {player.portraitImageLink && player.jerseyNumber != null && (
                    <Text
                      size="xs"
                      fw={700}
                      c="blumine"
                      tt="uppercase"
                      style={{ letterSpacing: "0.04em" }}
                    >
                      Nr. {player.jerseyNumber}
                    </Text>
                  )}
                  <Text fw={700} c="blumine" truncate>
                    {formatRosterName(player.name)}
                  </Text>
                  {player.position && (
                    <Text c="dimmed" size="xs" truncate>
                      {player.position}
                    </Text>
                  )}
                </Stack>
              </Group>
            ))}
          </SimpleGrid>
        )}
        {officialsWithRole.length > 0 && (
          <Stack gap="sm">
            <Text fw={700} size="sm" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.06em" }}>
              Offizielle
            </Text>
            <SimpleGrid cols={{ base: 1, xs: 2, sm: 3 }} spacing="md">
              {officialsWithRole.map((official) => (
                <Group key={official.uuid} gap="sm" wrap="nowrap" align="center">
                  <Avatar
                    name={formatRosterName(official.name)}
                    size={40}
                    radius="xl"
                    color="blumine"
                  />
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Text fw={700} c="blumine" truncate>
                      {formatRosterName(official.name)}
                    </Text>
                    <Text c="dimmed" size="xs" truncate>
                      {official.role}
                    </Text>
                  </Stack>
                </Group>
              ))}
            </SimpleGrid>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function TeamPictures({ team }: { team: NonNullable<ReturnType<typeof useTeamBySlug>["data"]> }) {
  const { data: imageUrls } = useFileUrls(team.pictureS3Keys || []);

  if (!team.pictureS3Keys || team.pictureS3Keys.length === 0) return null;

  return (
    <Card>
      <CardTitle>Team Fotos</CardTitle>
      <ImageGallery images={imageUrls || []} />
    </Card>
  );
}
